import React from 'react';
import ModelsAPI from '../../api/models';
import { AgentModelChoice } from '../../api/settings';
import { AIModel } from '../../api/structs/model';
import ProviderModelPicker, {
  PickerModelOption,
  firstModelFor,
  modelOptionsFor,
  providerOptionsFor,
} from '../ui/ProviderModelPicker';
import { agentModelOptions } from './agentModelOptions';

interface AgentModelFieldProps {
  /** The user's choice; `null` is the system default. */
  value: AgentModelChoice | null;
  /** The system default, as the server's answer names it. */
  defaultChoice?: AgentModelChoice;
  onChange: (choice: AgentModelChoice | null) => void;
}

/**
 * The model the run's agents work on. The list is the chat models an agent can
 * run on; its first option is the system default, which saves `null` so the
 * choice follows the default if that ever changes.
 */
const AgentModelField: React.FC<AgentModelFieldProps> = ({ value, defaultChoice, onChange }) => {
  const [catalog, setCatalog] = React.useState<AIModel[]>([]);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    void ModelsAPI.getChatModels()
      .then((list) => {
        if (!cancelled) setCatalog(list ?? []);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const models = React.useMemo(() => agentModelOptions(catalog), [catalog]);

  // The default's name comes from the whole catalog: it need not be a model the list offers.
  const defaultName = defaultChoice
    ? catalog.find((m) => m.provider === defaultChoice.provider && m.id === defaultChoice.model)?.name ||
      defaultChoice.model
    : '';
  const defaultOption: PickerModelOption = {
    value: '',
    label: defaultName ? `Default (${defaultName})` : 'Default',
  };

  // On Default the provider control shows the default's provider, so the row has something to show.
  const provider = value?.provider ?? defaultChoice?.provider ?? models[0]?.provider ?? '';
  const model = value?.model ?? '';

  return (
    <div>
      <h4 className="text-[10px] uppercase tracking-widest text-[var(--app-text-muted)] mb-1">Agent model</h4>
      <p className="mb-2 text-[11px] text-[var(--app-text-muted)]">
        The model the run's agents (writer, director, continuity) work on. You can change it for one run on its
        plan card.
      </p>
      <ProviderModelPicker
        providerOptions={providerOptionsFor(models, provider)}
        provider={provider}
        onProviderChange={(next) => {
          // A provider has no model of its own: land on its first, so a pair never mixes providers.
          const first = firstModelFor(models, next);
          if (next !== provider && first) onChange({ provider: next, model: first });
        }}
        providerAriaLabel="Agent model provider"
        modelOptions={[defaultOption, ...modelOptionsFor(models, provider, model)]}
        model={model}
        onModelChange={(next) => onChange(next ? { provider, model: next } : null)}
        modelAriaLabel="Agent model"
      />
      {failed && (
        <p className="mt-1 text-[11px] text-amber-400">
          Couldn't load the model list; your choice is kept as it is.
        </p>
      )}
    </div>
  );
};

export default AgentModelField;
